/** Schedule A builder: Prefill From Parent (primary) · Apply Template · Start Blank. */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { btnGhost, btnPrimary, errorText, inputCls } from '../finance/financeUi';

const METHODS = [['prefill_parent', 'Prefill From Parent'], ['template', 'Apply Template'], ['blank', 'Start Blank']];
const DERIVATIONS = [['parent_plus_markup', 'Parent + markup'], ['explicit', 'Fixed amount'], ['bps_markup', 'Parent + bps'],
                     ['flat_markup', 'Parent + flat'], ['pass_through', 'Pass through']];
const Field = ({ label, children }) => (
  <label className="text-sm"><span className="mb-1 block text-xs text-muted-foreground">{label}</span>{children}</label>
);
const valueKey = (d) => (d === 'explicit' ? 'amount' : 'markup_amount');

function LinesTable({ items, setItems, showParent }) {
  const update = (i, patch) => setItems(items.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  return (
    <table className="w-full text-sm" data-testid="schedule-builder-lines">
      <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
        <tr><th className="py-2">Line</th><th className="py-2">Unit</th>{showParent && <th className="py-2">Parent</th>}<th className="py-2">Derivation</th><th className="py-2">Value</th></tr>
      </thead>
      <tbody>
        {items.map((row, i) => (
          <tr key={`${row.fee_code}-${i}`} className="border-t" data-testid={`builder-line-${i}`}>
            <td className="py-2 font-medium">{row.label || row.fee_code}</td>
            <td className="py-2 text-xs text-muted-foreground">{String(row.unit).replace(/_/g, ' ')}</td>
            {showParent && <td className="py-2 font-mono text-xs">{row.parent_amount != null ? Number(row.parent_amount).toFixed(4) : '—'}</td>}
            <td className="py-2">
              <select className={`${inputCls} w-40`} value={row.derivation || 'explicit'} data-testid={`builder-derivation-${i}`}
                      onChange={(e) => update(i, { derivation: e.target.value })}>
                {DERIVATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </td>
            <td className="py-2">
              {row.derivation === 'pass_through' ? <span className="text-xs text-muted-foreground">at parent cost</span> : (
                <input type="number" step="0.0001" className={`${inputCls} w-28`} data-testid={`builder-value-${i}`}
                       value={row[valueKey(row.derivation)] ?? ''} onChange={(e) => update(i, { [valueKey(row.derivation)]: e.target.value === '' ? null : Number(e.target.value) })} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ScheduleBuilder({ uid, user, platforms, templates, onSaved, onCancel }) {
  const sources = useMemo(() => [...new Set(platforms.map((p) => p.residual_report_source))], [platforms]);
  const [source, setSource] = useState(sources[0] || '');
  const options = platforms.filter((p) => p.residual_report_source === source);
  const [platform, setPlatform] = useState(options[0]?.platform || '');
  const [form, setForm] = useState({ economic_program: '', effective_from: new Date().toISOString().slice(0, 10), start_method: 'prefill_parent', template_id: '', reason: '' });
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => { setForm({ ...form, ...patch }); setDraft(null); };
  const pickSource = (s) => { setSource(s); setPlatform(platforms.find((p) => p.residual_report_source === s)?.platform || ''); setDraft(null); };
  const chosen = platforms.find((p) => p.platform === platform);

  const load = async () => {
    const q = new URLSearchParams({ platform, method: form.start_method, effective_from: form.effective_from, ...(form.template_id ? { template_id: form.template_id } : {}) });
    try { setDraft(await api.request(`/team-economics/${uid}/schedule-a/prefill?${q}`)); } catch (e) { toast.error(errorText(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.request(`/team-economics/${uid}/schedule-a/build`, { method: 'POST', body: JSON.stringify({
        platform, residual_report_source: source, processing_platform: chosen?.processing_platform, economic_program: form.economic_program || null,
        parent_schedule_id: draft.parent.schedule_id, effective_from: form.effective_from, start_method: form.start_method,
        template_id: form.template_id || null, items: draft.items, reason: form.reason }) });
      toast.success('Schedule A saved — applies from the effective date');
      onSaved();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 rounded-xl border bg-muted/20 p-4" data-testid="schedule-builder">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Assigned to"><p className="h-9 py-2 font-medium" data-testid="builder-assigned-to">{user.first_name} {user.last_name}</p></Field>
        <Field label="Role"><p className="h-9 py-2 capitalize" data-testid="builder-role">{String(user.role).replace(/_/g, ' ')}</p></Field>
        <Field label="Residual report source">
          <select className={inputCls} value={source} onChange={(e) => pickSource(e.target.value)} data-testid="builder-report-source">
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Processing platform">
          <select className={inputCls} value={platform} onChange={(e) => { setPlatform(e.target.value); setDraft(null); }} data-testid="builder-processing-platform">
            {options.map((p) => <option key={p.platform} value={p.platform}>{p.processing_platform || p.label}</option>)}
          </select>
        </Field>
        <Field label="Economic program"><input className={inputCls} value={form.economic_program} placeholder="e.g. Standard" onChange={(e) => set({ economic_program: e.target.value })} data-testid="builder-economic-program" /></Field>
        <Field label="Effective date"><input type="date" className={inputCls} value={form.effective_from} onChange={(e) => set({ effective_from: e.target.value })} data-testid="builder-effective-date" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-2" data-testid="builder-start-methods">
        {METHODS.map(([v, l]) => (
          <button key={v} type="button" onClick={() => set({ start_method: v })} data-testid={`builder-method-${v}`}
                  className={`h-8 rounded-full px-3 text-xs transition-colors ${form.start_method === v ? 'bg-primary text-primary-foreground' : 'border hover:bg-muted'}`}>{l}</button>
        ))}
        {form.start_method === 'template' && (
          <select className={`${inputCls} sm:w-64`} value={form.template_id} onChange={(e) => set({ template_id: e.target.value })} data-testid="builder-template-select">
            <option value="">Choose a Schedule template…</option>
            {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
          </select>
        )}
        <button type="button" className={btnGhost} disabled={!platform || (form.start_method === 'template' && !form.template_id)} onClick={load} data-testid="builder-load-btn">Load lines</button>
      </div>
      {chosen && !chosen.cost_basis_ready && <p className="text-xs text-amber-700" data-testid="builder-cost-pending">MSC has not activated a cost basis for this platform yet.</p>}
      {draft && (
        <>
          <p className="text-xs text-muted-foreground" data-testid="builder-parent">Parent: {draft.parent.layer}{draft.parent.name ? ` · ${draft.parent.name}` : ''}</p>
          <LinesTable items={draft.items} setItems={(items) => setDraft({ ...draft, items })} showParent={draft.parent.amounts_visible} />
          <div className="flex flex-wrap gap-2">
            <input className={`${inputCls} sm:w-72`} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason (e.g. signed agreement)" data-testid="builder-reason" />
            <button type="button" className={btnPrimary} disabled={busy} onClick={save} data-testid="builder-save-btn">Save Schedule A</button>
            <button type="button" className={btnGhost} onClick={onCancel} data-testid="builder-cancel-btn">Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}
